import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useSidebar } from '@/components/ui/sidebar';
import { NAV_SECTIONS } from '@/components/nav-config';

/**
 * Sidebar search entry point. Renders a search-field-styled trigger (a compact
 * icon when the sidebar is collapsed) and a ⌘K command palette over every nav
 * destination.
 */
export function NavSearch() {
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  const go = (url: string) => {
    setOpen(false);
    navigate({ to: url });
  };

  return (
    <div className="flex justify-center px-2 group-data-[collapsible=icon]:px-0">
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Search"
        className={
          collapsed
            ? 'flex size-8 items-center justify-center rounded-lg border bg-background text-muted-foreground transition-all duration-200 hover:bg-accent hover:text-foreground active:scale-[0.98]'
            : 'flex h-9 w-full items-center gap-2 rounded-lg border bg-background px-3 text-sm text-muted-foreground shadow-xs transition-all duration-200 hover:bg-accent hover:text-foreground active:scale-[0.98]'
        }
      >
        <Search className="size-4 shrink-0" />
        {!collapsed && (
          <>
            <span>Search…</span>
            <kbd className="ml-auto inline-flex items-center gap-0.5 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">⌘</span>K
            </kbd>
          </>
        )}
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages…" />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {NAV_SECTIONS.map((section) => (
            <CommandGroup key={section.label} heading={section.label}>
              {section.items.map((item) => (
                <React.Fragment key={item.title}>
                  <CommandItem
                    value={item.title}
                    onSelect={() => go(item.url)}
                  >
                    <item.icon className="text-muted-foreground" />
                    <span>{item.title}</span>
                  </CommandItem>
                  {item.items?.map((leaf) => (
                    <CommandItem
                      key={leaf.url}
                      value={`${item.title} ${leaf.title}`}
                      onSelect={() => go(leaf.url)}
                    >
                      <item.icon className="text-muted-foreground opacity-50" />
                      <span className="text-muted-foreground">
                        {item.title}
                      </span>
                      <span className="text-muted-foreground/60">/</span>
                      <span>{leaf.title}</span>
                    </CommandItem>
                  ))}
                </React.Fragment>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
